// =============================================================================
// VulnTracker — Components Page (with Project Context & Add Component)
// =============================================================================
import { Fragment, useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Dialog, Transition } from '@headlessui/react';
import {
  PlusIcon,
  XMarkIcon,
  CubeIcon,
  ArrowUpTrayIcon,
  FunnelIcon,
  InformationCircleIcon,
  PencilSquareIcon,
  TrashIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useComponents, useUploadSBOM, useCreateComponent, useUpdateComponent, useDeleteComponent, useProjects, useScanAllComponents, useScanProgress } from '@/api/hooks';
import type { ScanProgress } from '@/api/hooks';
import { Pagination } from '@/components/Pagination';
import type { Component, ComponentEcosystem, Project } from '@/types';

// ── Reference Data (CycloneDX standard) ──────────────────────────────────
const COMPONENT_TYPES = [
  { value: 'library', label: 'Library' },
  { value: 'framework', label: 'Framework' },
  { value: 'application', label: 'Application' },
  { value: 'container', label: 'Container' },
  { value: 'device', label: 'Device' },
  { value: 'firmware', label: 'Firmware' },
  { value: 'operating-system', label: 'Operating System' },
  { value: 'file', label: 'File' },
  { value: 'other', label: 'Other' },
];

const ECOSYSTEMS = [
  { value: 'npm', label: 'npm' },
  { value: 'pypi', label: 'PyPI' },
  { value: 'maven', label: 'Maven' },
  { value: 'golang', label: 'Go' },
  { value: 'cargo', label: 'Cargo' },
  { value: 'nuget', label: 'NuGet' },
  { value: 'rubygems', label: 'RubyGems' },
  { value: 'composer', label: 'Composer' },
  { value: 'debian', label: 'Debian' },
  { value: 'alpine', label: 'Alpine' },
  { value: 'other', label: 'Other' },
];

const SCOPE_OPTIONS = [
  { value: '', label: 'Not specified' },
  { value: 'required', label: 'Required' },
  { value: 'optional', label: 'Optional' },
  { value: 'excluded', label: 'Excluded' },
];

const HASH_ALGORITHMS = [
  { value: '', label: 'None' },
  { value: 'SHA-256', label: 'SHA-256' },
  { value: 'SHA-384', label: 'SHA-384' },
  { value: 'SHA-512', label: 'SHA-512' },
  { value: 'SHA-1', label: 'SHA-1' },
  { value: 'MD5', label: 'MD5' },
];

// ── Info Tooltip (Portal-based — renders outside modal DOM) ──────────────
function InfoTip({ text }: { text: string }) {
  const [show, setShow] = useState(false);
  const iconRef = useRef<HTMLSpanElement>(null);
  const [style, setStyle] = useState<React.CSSProperties>({});
  const [arrowSide, setArrowSide] = useState<'left' | 'right'>('left');

  const handleEnter = useCallback(() => {
    if (!iconRef.current) return;
    const rect = iconRef.current.getBoundingClientRect();
    const tipW = 248; // w-60 = 15rem ≈ 240 + padding
    const gap = 8;

    // Prefer right side; if it would overflow viewport, flip to left
    if (rect.right + gap + tipW < window.innerWidth) {
      setStyle({
        position: 'fixed',
        top: rect.top + rect.height / 2,
        left: rect.right + gap,
        transform: 'translateY(-50%)',
      });
      setArrowSide('left');
    } else {
      setStyle({
        position: 'fixed',
        top: rect.top + rect.height / 2,
        left: rect.left - gap - tipW,
        transform: 'translateY(-50%)',
      });
      setArrowSide('right');
    }
    setShow(true);
  }, []);

  return (
    <span
      ref={iconRef}
      className="inline-flex ml-1 align-middle"
      onMouseEnter={handleEnter}
      onMouseLeave={() => setShow(false)}
    >
      <InformationCircleIcon className="h-4 w-4 text-gray-400 hover:text-primary-600 cursor-help" />
      {show &&
        createPortal(
          <span
            className="w-60 rounded-lg bg-gray-800 px-3 py-2 text-xs text-white shadow-lg text-center leading-relaxed pointer-events-none"
            style={{ ...style, zIndex: 99999 }}
          >
            {text}
            {arrowSide === 'left' && (
              <span className="absolute top-1/2 -translate-y-1/2 -left-1.5 border-[6px] border-transparent border-r-gray-800" />
            )}
            {arrowSide === 'right' && (
              <span className="absolute top-1/2 -translate-y-1/2 -right-1.5 border-[6px] border-transparent border-l-gray-800" />
            )}
          </span>,
          document.body,
        )}
    </span>
  );
}

// ── Add Component Modal ──────────────────────────────────────────────────
function AddComponentModal({
  open,
  onClose,
  projectId,
  projectName,
}: {
  open: boolean;
  onClose: () => void;
  projectId: string;
  projectName: string;
}) {
  const [name, setName] = useState('');
  const [version, setVersion] = useState('');
  const [ecosystem, setEcosystem] = useState('npm');
  const [componentType, setComponentType] = useState('library');
  const [group, setGroup] = useState('');
  const [supplier, setSupplier] = useState('');
  const [purl, setPurl] = useState('');
  const [cpe, setCpe] = useState('');
  const [licenseId, setLicenseId] = useState('');
  const [description, setDescription] = useState('');
  const [scope, setScope] = useState('');
  const [isDirect, setIsDirect] = useState(true);
  const [hashAlg, setHashAlg] = useState('');
  const [hashValue, setHashValue] = useState('');
  const createComponent = useCreateComponent();

  const isValid = name.trim().length > 0 && version.trim().length > 0;

  // Auto-generate PURL from ecosystem, group, name, version
  const generatedPurl = useMemo(() => {
    if (!name.trim() || !version.trim()) return '';
    const ecoMap: Record<string, string> = {
      npm: 'npm', pypi: 'pypi', maven: 'maven', golang: 'golang',
      cargo: 'cargo', nuget: 'nuget', rubygems: 'gem', composer: 'composer', other: 'generic',
    };
    const purlType = ecoMap[ecosystem] || 'generic';
    const ns = group.trim() ? `/${encodeURIComponent(group.trim())}` : '';
    return `pkg:${purlType}${ns}/${encodeURIComponent(name.trim())}@${encodeURIComponent(version.trim())}`;
  }, [ecosystem, group, name, version]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;

    try {
      await createComponent.mutateAsync({
        project_id: projectId,
        name: name.trim(),
        version: version.trim(),
        ecosystem,
        component_type: componentType,
        group: group.trim() || undefined,
        supplier: supplier.trim() || undefined,
        purl: purl.trim() || generatedPurl || undefined,
        cpe: cpe.trim() || undefined,
        license_id: licenseId.trim() || undefined,
        description: description.trim() || undefined,
        scope: scope || undefined,
        is_direct: isDirect,
        hash_alg: hashAlg || undefined,
        hash_value: hashValue.trim() || undefined,
      });
      toast.success(`Component "${name}" added successfully!`);
      resetForm();
      onClose();
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosErr = err as { response?: { data?: { detail?: string } } };
        toast.error(axiosErr.response?.data?.detail || 'Failed to add component');
      } else {
        toast.error('Failed to add component. Please try again.');
      }
    }
  };

  const resetForm = () => {
    setName(''); setVersion(''); setEcosystem('npm'); setComponentType('library');
    setGroup(''); setSupplier(''); setPurl(''); setCpe(''); setLicenseId('');
    setDescription(''); setScope(''); setIsDirect(true); setHashAlg(''); setHashValue('');
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 sm:items-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300" enterFrom="opacity-0 translate-y-4 sm:scale-95" enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200" leaveFrom="opacity-100 translate-y-0 sm:scale-100" leaveTo="opacity-0 translate-y-4 sm:scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-2xl transform rounded-xl bg-white shadow-2xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                  <div>
                    <Dialog.Title className="text-lg font-semibold text-gray-900">
                      Add Component
                    </Dialog.Title>
                    <p className="mt-0.5 text-sm text-gray-500">
                      Project: <span className="font-medium text-gray-700">{projectName}</span>
                      <span className="mx-2 text-gray-300">·</span>
                      <span className="text-xs text-gray-400">CycloneDX aligned</span>
                    </p>
                  </div>
                  <button onClick={onClose} className="rounded-md p-1 text-gray-400 hover:text-gray-500">
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
                  {/* Row 1: Name & Version (required) */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Component Name <span className="text-red-500">*</span>
                        <InfoTip text="The name of the software component or library (e.g. openssl, lodash, spring-core)." />
                      </label>
                      <input
                        type="text" required className="input-field px-3"
                        placeholder="e.g. openssl, lodash, spring-core"
                        value={name} onChange={(e) => setName(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Version <span className="text-red-500">*</span>
                        <InfoTip text="The exact version of the component used in your project (e.g. 3.0.12, 1.2.0). Used for vulnerability matching." />
                      </label>
                      <input
                        type="text" required className="input-field px-3"
                        placeholder="e.g. 3.0.12, 1.2.0"
                        value={version} onChange={(e) => setVersion(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Row 2: Type & Ecosystem */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Component Type
                        <InfoTip text="The type of component as defined by the CycloneDX standard — library, framework, application, container, firmware, etc." />
                      </label>
                      <select className="input-field px-3" value={componentType} onChange={(e) => setComponentType(e.target.value)}>
                        {COMPONENT_TYPES.map((t) => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Ecosystem <span className="text-red-500">*</span>
                        <InfoTip text="The package ecosystem or registry this component belongs to (e.g. npm for JavaScript, PyPI for Python, Maven for Java)." />
                      </label>
                      <select className="input-field px-3" value={ecosystem} onChange={(e) => setEcosystem(e.target.value)}>
                        {ECOSYSTEMS.map((eco) => (
                          <option key={eco.value} value={eco.value}>{eco.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Row 3: Group & Supplier */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Group / Namespace
                        <InfoTip text="The grouping or namespace of the component — e.g. Maven groupId (org.apache.commons), npm scope (@angular), or Python namespace." />
                      </label>
                      <input
                        type="text" className="input-field px-3"
                        placeholder="e.g. org.apache.commons, @angular"
                        value={group} onChange={(e) => setGroup(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Supplier / Vendor
                        <InfoTip text="The organization or individual that supplies or maintains this component (e.g. Apache Software Foundation, Google)." />
                      </label>
                      <input
                        type="text" className="input-field px-3"
                        placeholder="e.g. Apache Software Foundation"
                        value={supplier} onChange={(e) => setSupplier(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Row 4: PURL (auto-generated) & CPE */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Package URL (PURL)
                        <InfoTip text="A standardized identifier for this package. Auto-generated from ecosystem, group, name, and version. Override only if the auto value is incorrect." />
                      </label>
                      <input
                        type="text" className="input-field px-3 font-mono text-xs"
                        placeholder={generatedPurl || 'pkg:ecosystem/name@version'}
                        value={purl} onChange={(e) => setPurl(e.target.value)}
                      />
                      {generatedPurl && !purl && (
                        <p className="text-xs text-green-600 mt-1">Auto: {generatedPurl}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        CPE
                        <InfoTip text="Common Platform Enumeration — a structured identifier used by NVD to match components to known CVEs. Format: cpe:2.3:a:vendor:product:version:…" />
                      </label>
                      <input
                        type="text" className="input-field px-3 font-mono text-xs"
                        placeholder="cpe:2.3:a:vendor:product:version:*:*:*:*:*:*:*"
                        value={cpe} onChange={(e) => setCpe(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Row 5: License & Scope & Direct */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        License (SPDX ID)
                        <InfoTip text="The SPDX license identifier for this component (e.g. MIT, Apache-2.0, GPL-3.0-only). Refer to spdx.org/licenses for the full list." />
                      </label>
                      <input
                        type="text" className="input-field px-3"
                        placeholder="e.g. Apache-2.0, MIT"
                        value={licenseId} onChange={(e) => setLicenseId(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Scope
                        <InfoTip text="Defines whether this component is required at runtime, optional, or excluded from the final build." />
                      </label>
                      <select className="input-field px-3" value={scope} onChange={(e) => setScope(e.target.value)}>
                        {SCOPE_OPTIONS.map((s) => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-end">
                      <label className="flex items-center gap-2 cursor-pointer pb-2">
                        <input
                          type="checkbox" className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          checked={isDirect} onChange={(e) => setIsDirect(e.target.checked)}
                        />
                        <span className="text-sm font-medium text-gray-700">
                          Direct dependency
                          <InfoTip text="Check if this component is a direct (top-level) dependency. Uncheck if it is a transitive (indirect) dependency pulled in by another package." />
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* Row 6: Hash */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Hash Algorithm
                        <InfoTip text="The hashing algorithm used to compute the component's digest for integrity verification (e.g. SHA-256, SHA-512)." />
                      </label>
                      <select className="input-field px-3" value={hashAlg} onChange={(e) => setHashAlg(e.target.value)}>
                        {HASH_ALGORITHMS.map((h) => (
                          <option key={h.value} value={h.value}>{h.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Hash Value
                        <InfoTip text="The hex-encoded hash digest of the component artifact, used to verify the package has not been tampered with." />
                      </label>
                      <input
                        type="text" className="input-field px-3 font-mono text-xs"
                        placeholder="Hash digest" disabled={!hashAlg}
                        value={hashValue} onChange={(e) => setHashValue(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                      <InfoTip text="An optional short description of what this component does or why it is included in the project." />
                    </label>
                    <textarea
                      className="input-field px-3 py-2" rows={2}
                      placeholder="Brief description of the component"
                      value={description} onChange={(e) => setDescription(e.target.value)}
                    />
                  </div>
                </form>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
                  <button type="button" className="btn-secondary" onClick={() => { resetForm(); onClose(); }}>
                    Cancel
                  </button>
                  <button
                    type="submit" className="btn-primary gap-2"
                    disabled={!isValid || createComponent.isPending}
                    onClick={handleSubmit}
                  >
                    {createComponent.isPending ? (
                      <>
                        <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                        Adding…
                      </>
                    ) : (
                      <>
                        <CubeIcon className="h-4 w-4" />
                        Add Component
                      </>
                    )}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

// ── Edit Component Modal ───────────────────────────────────────────────
function EditComponentModal({
  open,
  onClose,
  component,
}: {
  open: boolean;
  onClose: () => void;
  component: Component;
}) {
  const [name, setName] = useState(component.name);
  const [version, setVersion] = useState(component.version);
  const [ecosystem, setEcosystem] = useState(component.ecosystem);
  const [componentType, setComponentType] = useState(component.component_type || 'library');
  const [group, setGroup] = useState(component.group || '');
  const [supplier, setSupplier] = useState(component.supplier || '');
  const [purl, setPurl] = useState(component.purl || '');
  const [cpe, setCpe] = useState(component.cpe || '');
  const [licenseId, setLicenseId] = useState(component.license_id || '');
  const [description, setDescription] = useState(component.description || '');
  const [scope, setScope] = useState(component.scope || '');
  const [isDirect, setIsDirect] = useState(component.is_direct);
  const [hashAlg, setHashAlg] = useState(component.hash_alg || '');
  const [hashValue, setHashValue] = useState(component.hash_value || '');
  const updateComponent = useUpdateComponent();

  // Sync form when component changes
  useEffect(() => {
    setName(component.name);
    setVersion(component.version);
    setEcosystem(component.ecosystem);
    setComponentType(component.component_type || 'library');
    setGroup(component.group || '');
    setSupplier(component.supplier || '');
    setPurl(component.purl || '');
    setCpe(component.cpe || '');
    setLicenseId(component.license_id || '');
    setDescription(component.description || '');
    setScope(component.scope || '');
    setIsDirect(component.is_direct);
    setHashAlg(component.hash_alg || '');
    setHashValue(component.hash_value || '');
  }, [component]);

  const isValid = name.trim().length > 0 && version.trim().length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;
    try {
      await updateComponent.mutateAsync({
        id: component.id,
        name: name.trim(),
        version: version.trim(),
        ecosystem,
        component_type: componentType,
        group: group.trim() || undefined,
        supplier: supplier.trim() || undefined,
        purl: purl.trim() || undefined,
        cpe: cpe.trim() || undefined,
        license_id: licenseId.trim() || undefined,
        description: description.trim() || undefined,
        scope: scope || undefined,
        is_direct: isDirect,
        hash_alg: hashAlg || undefined,
        hash_value: hashValue.trim() || undefined,
      });
      toast.success(`Component "${name}" updated successfully!`);
      onClose();
    } catch {
      toast.error('Failed to update component.');
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
        </Transition.Child>
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 sm:items-center">
            <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 translate-y-4 sm:scale-95" enterTo="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 translate-y-0 sm:scale-100" leaveTo="opacity-0 translate-y-4 sm:scale-95">
              <Dialog.Panel className="relative w-full max-w-2xl transform rounded-xl bg-white shadow-2xl transition-all">
                <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                  <Dialog.Title className="text-lg font-semibold text-gray-900">Edit Component</Dialog.Title>
                  <button onClick={onClose} className="rounded-md p-1 text-gray-400 hover:text-gray-500"><XMarkIcon className="h-5 w-5" /></button>
                </div>
                <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
                  {/* Row 1: Name & Version */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Component Name <span className="text-red-500">*</span></label>
                      <input type="text" required className="input-field px-3" value={name} onChange={(e) => setName(e.target.value)} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Version <span className="text-red-500">*</span></label>
                      <input type="text" required className="input-field px-3" value={version} onChange={(e) => setVersion(e.target.value)} />
                    </div>
                  </div>
                  {/* Row 2: Type & Ecosystem */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Component Type</label>
                      <select className="input-field px-3" value={componentType} onChange={(e) => setComponentType(e.target.value)}>
                        {COMPONENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Ecosystem <span className="text-red-500">*</span></label>
                      <select className="input-field px-3" value={ecosystem} onChange={(e) => setEcosystem(e.target.value as ComponentEcosystem)}>
                        {ECOSYSTEMS.map((eco) => <option key={eco.value} value={eco.value}>{eco.label}</option>)}
                      </select>
                    </div>
                  </div>
                  {/* Row 3: Group & Supplier */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Group / Namespace</label>
                      <input type="text" className="input-field px-3" value={group} onChange={(e) => setGroup(e.target.value)} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Supplier / Vendor</label>
                      <input type="text" className="input-field px-3" value={supplier} onChange={(e) => setSupplier(e.target.value)} />
                    </div>
                  </div>
                  {/* Row 4: PURL & CPE */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Package URL (PURL)</label>
                      <input type="text" className="input-field px-3 font-mono text-xs" value={purl} onChange={(e) => setPurl(e.target.value)} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">CPE</label>
                      <input type="text" className="input-field px-3 font-mono text-xs" value={cpe} onChange={(e) => setCpe(e.target.value)} />
                    </div>
                  </div>
                  {/* Row 5: License, Scope, Direct */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">License (SPDX ID)</label>
                      <input type="text" className="input-field px-3" value={licenseId} onChange={(e) => setLicenseId(e.target.value)} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Scope</label>
                      <select className="input-field px-3" value={scope} onChange={(e) => setScope(e.target.value)}>
                        {SCOPE_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </div>
                    <div className="flex items-end">
                      <label className="flex items-center gap-2 cursor-pointer pb-2">
                        <input type="checkbox" className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500" checked={isDirect} onChange={(e) => setIsDirect(e.target.checked)} />
                        <span className="text-sm font-medium text-gray-700">Direct dependency</span>
                      </label>
                    </div>
                  </div>
                  {/* Row 6: Hash */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Hash Algorithm</label>
                      <select className="input-field px-3" value={hashAlg} onChange={(e) => setHashAlg(e.target.value)}>
                        {HASH_ALGORITHMS.map((h) => <option key={h.value} value={h.value}>{h.label}</option>)}
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Hash Value</label>
                      <input type="text" className="input-field px-3 font-mono text-xs" disabled={!hashAlg} value={hashValue} onChange={(e) => setHashValue(e.target.value)} />
                    </div>
                  </div>
                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea className="input-field px-3 py-2" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
                  </div>
                </form>
                <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
                  <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
                  <button type="submit" className="btn-primary gap-2" disabled={!isValid || updateComponent.isPending} onClick={handleSubmit}>
                    {updateComponent.isPending ? (
                      <><span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />Saving…</>
                    ) : (
                      <><PencilSquareIcon className="h-4 w-4" />Save Changes</>
                    )}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

// ── Delete Component Confirm Dialog ───────────────────────────────────
function DeleteComponentDialog({
  open,
  onClose,
  component,
}: {
  open: boolean;
  onClose: () => void;
  component: Component;
}) {
  const deleteComponent = useDeleteComponent();

  const handleDelete = async () => {
    try {
      await deleteComponent.mutateAsync(component.id);
      toast.success(`Component "${component.name}" deleted.`);
      onClose();
    } catch {
      toast.error('Failed to delete component.');
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
        </Transition.Child>
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 sm:items-center">
            <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 translate-y-4 sm:scale-95" enterTo="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 translate-y-0 sm:scale-100" leaveTo="opacity-0 translate-y-4 sm:scale-95">
              <Dialog.Panel className="relative w-full max-w-md transform rounded-xl bg-white shadow-2xl transition-all">
                <div className="px-6 py-6">
                  <div className="flex items-start gap-4">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-red-100">
                      <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
                    </div>
                    <div>
                      <Dialog.Title className="text-lg font-semibold text-gray-900">Delete Component</Dialog.Title>
                      <p className="mt-2 text-sm text-gray-600">
                        Are you sure you want to delete <span className="font-semibold text-gray-900">{component.name}@{component.version}</span>?
                      </p>
                      <div className="mt-3 rounded-lg bg-red-50 border border-red-200 p-3">
                        <p className="text-xs font-semibold text-red-800 mb-1">This action is permanent and cannot be undone.</p>
                        <ul className="text-xs text-red-700 list-disc list-inside space-y-0.5">
                          <li>The component record will be removed</li>
                          <li>All associated vulnerability matches will be deleted</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
                  <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
                    onClick={handleDelete}
                    disabled={deleteComponent.isPending}
                  >
                    {deleteComponent.isPending ? (
                      <><span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />Deleting…</>
                    ) : (
                      <><TrashIcon className="h-4 w-4" />Delete Permanently</>
                    )}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────
export function ComponentsPage() {
  const [page, setPage] = useState(1);
  const [ecosystem, setEcosystem] = useState('');
  const [search, setSearch] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editComponent, setEditComponent] = useState<Component | null>(null);
  const [deleteComponent, setDeleteComponent] = useState<Component | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [sbomFormat, setSbomFormat] = useState('cyclonedx');

  // Fetch projects for the selector
  const { data: projectsData, isLoading: projectsLoading } = useProjects({ page_size: 100 });
  const projects: Project[] = projectsData?.items ?? [];

  // Auto-select first project when projects load
  const activeProjectId = selectedProjectId || (projects.length > 0 ? projects[0].id : '');
  const activeProject = projects.find((p) => p.id === activeProjectId);

  // Fetch components filtered by active project
  const { data, isLoading } = useComponents({
    page,
    page_size: 20,
    ...(activeProjectId && { project_id: activeProjectId }),
    ...(ecosystem && { ecosystem }),
    ...(search && { search }),
  });

  const uploadMutation = useUploadSBOM();
  const scanAllMutation = useScanAllComponents();
  const [scanTaskId, setScanTaskId] = useState<string | null>(null);
  const { data: scanProgress } = useScanProgress(scanTaskId);

  // True from the moment the user clicks "Upload & Parse" until everything finishes
  const isProcessing =
    uploadMutation.isPending ||
    scanAllMutation.isPending ||
    (!!scanTaskId && (scanProgress?.status === 'running' || scanProgress?.status === 'enriching'));

  // When scan completes, clear the task and invalidate
  useEffect(() => {
    if (scanProgress?.status === 'completed') {
      toast.success(
        `Scan complete! Found vulnerabilities across ${scanProgress.scanned} components.`,
      );
      const timer = setTimeout(() => setScanTaskId(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [scanProgress?.status]);

  const handleUpload = async () => {
    const file = fileInputRef.current?.files?.[0];
    if (!file || !activeProjectId) {
      toast.error('Please select a project and a file to upload');
      return;
    }
    try {
      const result = await uploadMutation.mutateAsync({
        projectId: activeProjectId,
        formatType: sbomFormat,
        file,
      });
      toast.success(
        `Imported ${result.components_added} new, updated ${result.components_updated} components`,
      );
      if (fileInputRef.current) fileInputRef.current.value = '';

      // Auto-trigger vulnerability scanning for all components in this project
      if (result.components_added > 0 || result.components_updated > 0) {
        try {
          const scanResult = await scanAllMutation.mutateAsync(activeProjectId);
          setScanTaskId(scanResult.task_id);
          toast('🔍 Scanning components for vulnerabilities…', { icon: '⏳' });
        } catch {
          toast.error('SBOM imported but vulnerability scan failed to start.');
        }
      }
    } catch {
      toast.error('SBOM upload failed');
    }
  };

  return (
    <div className="space-y-6 relative">
      {/* ── Full-screen blocking overlay while uploading / scanning ── */}
      {isProcessing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary-100">
              <div className="animate-spin rounded-full h-8 w-8 border-[3px] border-primary-600 border-t-transparent" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              {uploadMutation.isPending
                ? 'Uploading & Parsing SBOM…'
                : scanAllMutation.isPending
                  ? 'Starting vulnerability scan…'
                  : scanProgress?.status === 'enriching'
                    ? 'Enriching CVE data…'
                    : 'Scanning components for vulnerabilities…'}
            </h3>
            {scanProgress && scanProgress.status === 'running' && (
              <>
                <p className="text-sm text-gray-500 mb-4">
                  {scanProgress.scanned} of {scanProgress.total} components scanned
                  {scanProgress.vulnerabilities_found > 0 && (
                    <span className="text-amber-600 font-medium">
                      {' '}· {scanProgress.vulnerabilities_found} vuln{scanProgress.vulnerabilities_found !== 1 ? 's' : ''} found
                    </span>
                  )}
                </p>
                <div className="w-full bg-gray-200 rounded-full h-2.5 mb-3">
                  <div
                    className="h-2.5 rounded-full bg-primary-600 transition-all duration-300"
                    style={{ width: `${scanProgress.total > 0 ? Math.round((scanProgress.scanned / scanProgress.total) * 100) : 0}%` }}
                  />
                </div>
                {scanProgress.current_component && (
                  <p className="text-xs text-gray-400 truncate">
                    Currently scanning: <span className="font-mono">{scanProgress.current_component}</span>
                  </p>
                )}
              </>
            )}
            {scanProgress && scanProgress.status === 'enriching' && (
              <>
                <p className="text-sm text-gray-500 mb-4">
                  Fetching CVSS &amp; CWE data from NVD for matched CVEs…
                  {scanProgress.enrichment_total && scanProgress.enrichment_done !== undefined && (
                    <span className="text-primary-600 font-medium">
                      {' '}({scanProgress.enrichment_done}/{scanProgress.enrichment_total} CVEs)
                    </span>
                  )}
                </p>
                <div className="w-full bg-gray-200 rounded-full h-2.5 mb-3">
                  <div
                    className="h-2.5 rounded-full bg-amber-500 transition-all duration-300"
                    style={{ width: scanProgress.enrichment_total ? `${Math.round(((scanProgress.enrichment_done ?? 0) / scanProgress.enrichment_total) * 100)}%` : '100%' }}
                  />
                </div>
                <p className="text-xs text-gray-400">
                  {scanProgress.current_component || 'Enriching vulnerability data…'}
                </p>
              </>
            )}
            {!scanProgress && (
              <p className="text-sm text-gray-500">Please wait — do not navigate away.</p>
            )}
          </div>
        </div>
      )}

      {/* Header Row */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Components</h1>
          <p className="mt-1 text-sm text-gray-500">
            {data?.total ?? 0} component{(data?.total ?? 0) !== 1 ? 's' : ''} in inventory
            {activeProject && (
              <span className="ml-1">
                for <span className="font-medium text-gray-700">{activeProject.name}</span>
              </span>
            )}
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* Project Selector */}
          <div className="min-w-[220px]">
            <select
              className="input-field px-3 text-sm"
              value={activeProjectId}
              onChange={(e) => { setSelectedProjectId(e.target.value); setPage(1); }}
              disabled={projectsLoading || projects.length === 0}
            >
              {projectsLoading && <option value="">Loading projects…</option>}
              {!projectsLoading && projects.length === 0 && (
                <option value="">No projects — create one first</option>
              )}
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>

          {/* Add Component Button */}
          <button
            className="btn-primary gap-2 whitespace-nowrap"
            onClick={() => setShowAddModal(true)}
            disabled={!activeProjectId}
            title={!activeProjectId ? 'Select or create a project first' : 'Add a component'}
          >
            <PlusIcon className="h-4 w-4" />
            Add Component
          </button>
        </div>
      </div>

      {/* SBOM Upload Card */}
      <div className="card">
        <div className="flex items-center gap-2 mb-3">
          <ArrowUpTrayIcon className="h-5 w-5 text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-900">Import SBOM / Lockfile</h3>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Upload a CycloneDX, SPDX, or lockfile to bulk-import components into
          {activeProject ? ` "${activeProject.name}"` : ' the selected project'}.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Format</label>
            <select
              className="input-field px-3"
              value={sbomFormat}
              onChange={(e) => setSbomFormat(e.target.value)}
            >
              <option value="cyclonedx">CycloneDX</option>
              <option value="spdx">SPDX</option>
              <option value="npm">npm (package-lock.json)</option>
              <option value="pip">pip (requirements.txt)</option>
              <option value="maven">Maven</option>
              <option value="go">Go (go.sum)</option>
              <option value="cargo">Cargo (Cargo.lock)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">File</label>
            <input
              ref={fileInputRef}
              type="file"
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
            />
          </div>
          <button
            className="btn-primary gap-2"
            onClick={handleUpload}
            disabled={uploadMutation.isPending || scanAllMutation.isPending || !activeProjectId}
          >
            <ArrowUpTrayIcon className="h-4 w-4" />
            {uploadMutation.isPending ? 'Uploading…' : scanAllMutation.isPending ? 'Starting scan…' : 'Upload & Parse'}
          </button>
        </div>

        {/* Vulnerability Scan Progress Bar */}
        {scanTaskId && scanProgress && (
          <div className="mt-4 rounded-lg border border-primary-200 bg-primary-50 p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                {(scanProgress.status === 'running' || scanProgress.status === 'enriching') && (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-600 border-t-transparent" />
                )}
                {scanProgress.status === 'completed' && (
                  <svg className="h-4 w-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                )}
                <span className="text-sm font-medium text-primary-800">
                  {scanProgress.status === 'running'
                    ? `Scanning vulnerabilities… (${scanProgress.scanned}/${scanProgress.total})`
                    : scanProgress.status === 'enriching'
                      ? `Enriching CVE data… (${scanProgress.enrichment_done ?? 0}/${scanProgress.enrichment_total ?? '?'})`
                      : `Scan complete — ${scanProgress.scanned} components scanned`}
                </span>
              </div>
              <span className="text-xs text-primary-600">
                {scanProgress.vulnerabilities_found} vuln{scanProgress.vulnerabilities_found !== 1 ? 's' : ''} found
              </span>
            </div>
            {/* Progress bar */}
            <div className="w-full bg-primary-200 rounded-full h-2.5">
              <div
                className={`h-2.5 rounded-full transition-all duration-300 ${
                  scanProgress.status === 'completed' ? 'bg-green-600' : 'bg-primary-600'
                }`}
                style={{ width: `${scanProgress.total > 0 ? Math.round((scanProgress.scanned / scanProgress.total) * 100) : 0}%` }}
              />
            </div>
            {scanProgress.current_component && (scanProgress.status === 'running' || scanProgress.status === 'enriching') && (
              <p className="mt-1.5 text-xs text-primary-600 truncate">
                {scanProgress.status === 'enriching' ? '' : 'Currently scanning: '}
                <span className="font-mono">{scanProgress.current_component}</span>
              </p>
            )}

          </div>
        )}
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex items-center gap-2 mb-3">
          <FunnelIcon className="h-5 w-5 text-gray-400" />
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Filters</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text" className="input-field px-3"
              placeholder="Package name…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ecosystem</label>
            <select
              className="input-field px-3"
              value={ecosystem}
              onChange={(e) => { setEcosystem(e.target.value); setPage(1); }}
            >
              <option value="">All</option>
              {ECOSYSTEMS.map((eco) => (
                <option key={eco.value} value={eco.value}>{eco.label}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              className="btn-secondary w-full"
              onClick={() => { setEcosystem(''); setSearch(''); setPage(1); }}
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden p-0">
        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Version</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ecosystem</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Supplier</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">License</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Direct</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">PURL</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {(data?.items ?? []).map((comp: Component) => (
                    <tr key={comp.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        <div className="flex items-center gap-2">
                          <CubeIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          <div>
                            <div>{comp.name}</div>
                            {comp.group && <div className="text-xs text-gray-400">{comp.group}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 font-mono">
                        {comp.version && comp.version !== 'unknown' ? comp.version : (
                          <span className="text-gray-400 italic">{comp.version || 'unknown'}</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 capitalize">{comp.component_type || 'library'}</td>
                      <td className="px-6 py-4">
                        <span className="badge bg-primary-100 text-primary-800">{comp.ecosystem}</span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">{comp.supplier || '—'}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {comp.license_id ? (
                          <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-600/10">
                            {comp.license_id}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">{comp.is_direct ? '✓' : '—'}</td>
                      <td className="px-6 py-4 text-xs text-gray-400 font-mono truncate max-w-[200px]">
                        {comp.purl || '—'}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => setEditComponent(comp)}
                            className="rounded-md p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-colors"
                            title="Edit component"
                          >
                            <PencilSquareIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => setDeleteComponent(comp)}
                            className="rounded-md p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                            title="Delete component"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {(data?.items ?? []).length === 0 && (
                    <tr>
                      <td colSpan={9} className="px-6 py-16 text-center">
                        <CubeIcon className="mx-auto h-10 w-10 text-gray-300" />
                        <p className="mt-3 text-sm font-medium text-gray-900">No components found</p>
                        <p className="mt-1 text-sm text-gray-500">
                          {activeProjectId
                            ? 'Add a component manually or upload an SBOM to get started.'
                            : 'Select a project first, then add components.'}
                        </p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <Pagination
              page={page}
              totalPages={data?.total_pages ?? 1}
              onPageChange={setPage}
            />
          </>
        )}
      </div>

      {/* Add Component Modal */}
      {activeProjectId && (
        <AddComponentModal
          open={showAddModal}
          onClose={() => setShowAddModal(false)}
          projectId={activeProjectId}
          projectName={activeProject?.name ?? 'Unknown'}
        />
      )}

      {/* Edit Component Modal */}
      {editComponent && (
        <EditComponentModal
          open={!!editComponent}
          onClose={() => setEditComponent(null)}
          component={editComponent}
        />
      )}

      {/* Delete Component Confirm */}
      {deleteComponent && (
        <DeleteComponentDialog
          open={!!deleteComponent}
          onClose={() => setDeleteComponent(null)}
          component={deleteComponent}
        />
      )}
    </div>
  );
}
